import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/de';
import {
  useListLogEntriesQuery,
  useCreateLogEntryMutation,
  PAGE_SIZE,
} from '../../redux/api/logbookApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import LogEntryCard from './LogEntryCard';

const dayHeading = (isoDate) => {
  const d = moment(isoDate, 'YYYY-MM-DD').locale('de');
  const today = moment().startOf('day');
  const diff = d.startOf('day').diff(today, 'days');
  if (diff === 0) return 'Heute';
  if (diff === -1) return 'Gestern';
  return d.format('dddd, DD. MMMM YYYY');
};

const Logbook = () => {
  const currentEvent = useSelector((state) => state.auth.currentEvent);
  const apiErrorToast = useApiErrorToast();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [body, setBody] = useState('');

  // Debounce the search box; reset paging whenever the term changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching, isLoading } = useListLogEntriesQuery(
    { eventId: currentEvent?.id, search: search || undefined, offset, limit: PAGE_SIZE },
    { skip: !currentEvent?.id }
  );

  const [createEntry, { isLoading: creating }] = useCreateLogEntryMutation();

  const items = useMemo(() => data?.items || [], [data?.items]);
  const hasMore = Boolean(data?.next);

  // Group consecutive entries by their entry_date (already newest-first).
  const groups = useMemo(() => {
    const out = [];
    let current = null;
    items.forEach((entry) => {
      if (!current || current.date !== entry.entry_date) {
        current = { date: entry.entry_date, items: [] };
        out.push(current);
      }
      current.items.push(entry);
    });
    return out;
  }, [items]);

  const handleCreate = async () => {
    const text = body.trim();
    if (!text) return;
    try {
      await createEntry({ event: currentEvent.id, body: text }).unwrap();
      setBody('');
      setOffset(0);
      toast.success('Eintrag gespeichert');
    } catch (err) {
      apiErrorToast(err, 'Speichern fehlgeschlagen');
    }
  };

  if (!currentEvent?.id) {
    return (
      <div className='alert alert-info'>
        Bitte zuerst ein Event in der Top-Navigation auswählen.
      </div>
    );
  }

  return (
    <div className='container py-3 text-start' style={{ maxWidth: 820 }}>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h4 className='mb-0'>
          <i className='fas fa-book me-2' />
          Logbuch — {currentEvent.name}
        </h4>
      </div>

      {/* Composer */}
      <div className='card mb-4'>
        <div className='card-body'>
          <textarea
            className='form-control'
            rows={3}
            placeholder='Neuer Logbuch-Eintrag…'
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleCreate();
            }}
          />
          <div className='d-flex justify-content-between align-items-center mt-2'>
            <small className='text-muted'>⌘/Strg + Enter zum Speichern</small>
            <button
              className='btn btn-success'
              disabled={creating || !body.trim()}
              onClick={handleCreate}
            >
              <i className='fas fa-plus me-2' />
              Eintrag hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className='input-group mb-3'>
        <span className='input-group-text'>
          <i className='fas fa-search' />
        </span>
        <input
          type='text'
          className='form-control'
          placeholder='Einträge und Kommentare durchsuchen…'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {searchInput && (
          <button
            className='btn btn-outline-secondary'
            onClick={() => setSearchInput('')}
          >
            <i className='fas fa-times' />
          </button>
        )}
      </div>

      {search && !isFetching && (
        <p className='text-muted small'>
          {data?.count ?? 0} Treffer für „{search}“
        </p>
      )}

      {isLoading ? (
        <p>Wird geladen…</p>
      ) : groups.length === 0 ? (
        <div className='alert alert-light'>
          {search ? 'Keine Treffer.' : 'Noch keine Einträge.'}
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.date} className='mb-4'>
            <h6 className='text-uppercase text-muted border-bottom pb-1 mb-3'>
              {dayHeading(group.date)}
            </h6>
            {group.items.map((entry) => (
              <LogEntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        ))
      )}

      {hasMore && (
        <div className='text-center my-3'>
          <button
            className='btn btn-outline-primary'
            disabled={isFetching}
            onClick={() => setOffset(items.length)}
          >
            {isFetching ? 'Lädt…' : 'Mehr laden'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Logbook;
